import { curveBoundsFromRatio } from './cylinder-y';
import type { Rect } from './types';

/**
 * 원통 워프 입력. 마플 미리보기 설정(`preview.cylinder`)의 원값 px 를 그대로 받는다.
 * - area: 펼친 원본 영역(`cylinder.area`)
 * - upSide·downSide: 위·아래 경계의 양 끝 이동량(`cylinder.up_side`·`cylinder.down_side`)
 * - degree: 회전(`cylinder.degree`). 정면 직교 투영만 지원하므로 0 이어야 한다.
 */
export interface CylinderParams {
  readonly area: Rect;
  readonly upSide: number;
  readonly downSide: number;
  readonly degree: number;
}

/** 곡면 모양만 쓰는 계산(투영 영역·위아래 경계)의 입력. degree 는 보지 않는다. */
export type CylinderShape = Pick<CylinderParams, 'area' | 'upSide' | 'downSide'>;

/**
 * 원통 입력 문제.
 * - NON_FINITE: 숫자가 아니거나 무한대인 값이 있다. 이때는 다른 검사를 하지 않는다.
 * - NON_POSITIVE_SIZE: area 폭이나 높이가 0 이하다.
 * - UNSUPPORTED_DEGREE: degree 가 0 이 아니다. 정면 직교 투영만 지원한다(2026-10-06 확정).
 * - CURVES_CROSS: 가운데나 양 끝에서 계산한 위 경계가 아래 경계에 닿거나 넘는다.
 */
export type CylinderIssue =
  | 'NON_FINITE'
  | 'NON_POSITIVE_SIZE'
  | 'UNSUPPORTED_DEGREE'
  | 'CURVES_CROSS';

/**
 * 원통 입력을 검사해 문제 목록을 돌려준다. 문제가 없으면 빈 배열이다.
 * 열 c 의 위·아래 곡선 사이 높이는 area.height + (downSide − upSide)·(1 − e) 로 (1 − e) 에 대한 일차식이다.
 * 그래서 최솟값은 가운데(e = 1) 아니면 양 끝(e = 0) 이다.
 * CURVES_CROSS 는 이 두 열의 위·아래 경계를 점 변환과 같은 식(curveBoundsFromRatio)으로 계산해 비교한다.
 * 대수식으로는 양수여도 부동소수 반올림으로 두 경계가 같은 수가 되면 걸린다.
 */
export function validateCylinder(params: CylinderParams): readonly CylinderIssue[] {
  if (!hasFiniteValues(params)) return ['NON_FINITE'];

  const issues: CylinderIssue[] = [];
  const sized = hasPositiveSize(params);
  if (!sized) issues.push('NON_POSITIVE_SIZE');
  if (params.degree !== 0) issues.push('UNSUPPORTED_DEGREE');
  if (sized && !curvesStayApart(params)) issues.push('CURVES_CROSS');
  return issues;
}

/**
 * validateCylinder 가 빈 배열인지를 배열을 만들지 않고 판정한다. 점 변환처럼 자주 부르는 곳에서 쓴다.
 */
export function isValidCylinder(params: CylinderParams): boolean {
  return (
    hasFiniteValues(params) &&
    hasPositiveSize(params) &&
    params.degree === 0 &&
    curvesStayApart(params)
  );
}

function hasFiniteValues({ area, upSide, downSide, degree }: CylinderParams): boolean {
  return (
    Number.isFinite(area.left) &&
    Number.isFinite(area.top) &&
    Number.isFinite(area.width) &&
    Number.isFinite(area.height) &&
    Number.isFinite(upSide) &&
    Number.isFinite(downSide) &&
    Number.isFinite(degree)
  );
}

function hasPositiveSize({ area }: CylinderParams): boolean {
  return area.width > 0 && area.height > 0;
}

/** 가운데 열과 양 끝 열에서 점 변환과 같은 식으로 계산한 아래 경계가 위 경계보다 크다. */
function curvesStayApart(params: CylinderParams): boolean {
  const center = curveBoundsFromRatio(1, params);
  const ends = curveBoundsFromRatio(0, params);
  return center.bottom > center.top && ends.bottom > ends.top;
}
